<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, date, string } from 'yup'
import { useI18n } from 'vue-i18n'
import {
  DCard,
  DBtn,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DLoading
} from 'front-library'
import {
  getPayroll,
  getReportRequest,
  getListYear,
  ParametersInfonacotCredit,
  ReportInfonacotCredit,
  generateInfonacotCredit,
  getEmployerRecords
} from '@/app/core/services/reports'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsEmployerRecords = ref<any[]>([])
const itemsWorkCenters = ref<any[]>([])
const searchWorkCenters = ref<string>('')
const itemsYears = ref<any[]>([])
const searchMonths = ref<string>('')
const itemsMonths = ref([
  { id: '1', name: t('reports.months.january') },
  { id: '2', name: t('reports.months.february') },
  { id: '3', name: t('reports.months.march') },
  { id: '4', name: t('reports.months.april') },
  { id: '5', name: t('reports.months.may') },
  { id: '6', name: t('reports.months.june') },
  { id: '7', name: t('reports.months.july') },
  { id: '8', name: t('reports.months.august') },
  { id: '9', name: t('reports.months.september') },
  { id: '10', name: t('reports.months.october') },
  { id: '11', name: t('reports.months.november') },
  { id: '12', name: t('reports.months.december') }
])
const validGenerateReport = ref(true)
const urlFile = ref(null)
const refresh = ref()

const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  year: string().required(t('form.valid.required')),
  month: string().required(t('form.valid.required')),
  employerRecordId: string().required(t('form.valid.required')),
  workCenterId: string().when('employerRecordId', {
    is: (value: string) => value && value.length > 0,
    then: schema => schema.required(t('form.valid.required')),
    otherwise: schema => schema.notRequired()
  })
})

const { handleSubmit, errors } = useForm<ParametersInfonacotCredit>({
  validationSchema
})

const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: year } = useField<string>('year')
const { value: month } = useField<string>('month')
const { value: employerRecordId } = useField<string>('employerRecordId')
const { value: workCenterId } = useField<string>('workCenterId')

const updateWorkCenters = (selectedEmployerRecordId: string) => {
  workCenterId.value = ''
  itemsWorkCenters.value = []

  if (!selectedEmployerRecordId) {
    return
  }

  const selectedEmployerRecord = itemsEmployerRecords.value.find(
    record => record.id === selectedEmployerRecordId
  )

  if (selectedEmployerRecord) {
    if (
      selectedEmployerRecord.work_centers &&
      Array.isArray(selectedEmployerRecord.work_centers) &&
      selectedEmployerRecord.work_centers.length > 0
    ) {
      itemsWorkCenters.value = selectedEmployerRecord.work_centers.map(
        (workCenter: any) => ({
          id: workCenter.id,
          name: workCenter.code + ' - ' + workCenter.name
        })
      )
    } else {
      notifications.addNotification({
        title: t('reports.error.no-work-centers-found'),
        text: t('reports.error.no-work-centers-found'),
        type: 'warning',
        time: 8000
      })
    }
  }
}

watch(
  () => employerRecordId.value,
  newEmployerRecordId => {
    updateWorkCenters(newEmployerRecordId)
  },
  { immediate: true }
)

const getListEmployerRecords = async () => {
  if (!payrolls.value?.length) {
    itemsEmployerRecords.value = []
    employerRecordId.value = ''
    itemsWorkCenters.value = []
    workCenterId.value = ''
    return
  }

  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const companyId = storePayroll.payroll?.company.id
    if (companyId) {
      itemsEmployerRecords.value = await getEmployerRecords(companyId)
      if (itemsEmployerRecords.value.length === 0) {
        notifications.addNotification({
          title: t('reports.error.no-employer-records-found'),
          text: t('reports.error.no-employer-records-found'),
          type: 'warning',
          time: 10000
        })
      }
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

watch(
  () => payrolls.value,
  () => {
    getListEmployerRecords()
  },
  { deep: true }
)

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  if (validGenerateReport.value) {
    const path = route.params.code as string
    const decodeCode = atob(path)
    const createReport: ReportInfonacotCredit = {
      code: decodeCode,
      parameters: values
    }
    void generateReport(createReport)
  }
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateInfonacotCredit(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado de la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null

  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = (valid: boolean) => {
  showModal.value = false
  if (valid) {
    window.location.reload()
  }
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    itemsPayrolls.value = await getPayroll(store.user?.id as string)
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    itemsYears.value = getListYear()
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.infonacotCredit')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4 mb-3">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.year')"
                    v-model="year"
                    :required="true"
                    :options="itemsYears"
                    :error="errors.year" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.month')"
                    v-model="month"
                    :required="true"
                    :options="filterOptions(itemsMonths, searchMonths)"
                    :error="errors.month"
                    @filter="searchMonths = $event" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.employerRecord')"
                    v-model="employerRecordId"
                    :required="true"
                    :options="itemsEmployerRecords"
                    :error="errors.employerRecordId"
                    :disabled="!payrolls?.length" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.workCenter')"
                    v-model="workCenterId"
                    :required="true"
                    :options="
                      filterOptions(itemsWorkCenters, searchWorkCenters)
                    "
                    :error="errors.workCenterId"
                    :disabled="!employerRecordId || !itemsWorkCenters.length"
                    @filter="searchWorkCenters = $event" />
                </div>
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.generateFile')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
